import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import isString from 'lodash/isString';
import throttle from 'lodash/throttle';
import React, {Component, createRef} from 'react';
import {compose} from 'redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {connect} from 'react-redux';
import {Helmet} from 'react-helmet-async';
import {getWindow} from 'ssr-window';
import dayjs from 'utils/dayjs-duration';
import IconArrowLeft from '@spothero/icons/arrow-left';
import IconTimes from '@spothero/icons/x';
import {ClockPlus} from '@spothero/icons';
import {
    Flex,
    Text,
    Box,
    Button,
    Alert,
    Accordion,
    AccordionItem,
    AccordionPanel,
    AccordionButton,
    AccordionIcon,
    Heading,
} from '@spothero/ui';
import Image from 'common/image';
import DOMUtils from '@spothero/utils/dom';
import FacilitySummary from 'common/facility-summary/v1';
import FacilitySummaryV2 from 'common/facility-summary/v2';
import {push} from 'store/router/router-actions';
import {updateData} from 'store/search-request/search-request-actions';
import trackAvailabilityCheck from 'segment/events/availability-check';
import trackSpotDetails from 'segment/pages/spot-details';
import GTMUtils from 'utils/gtm';
import SpotUtils from 'utils/spot';
import TimeUtils from 'utils/time';
import {updateSelected} from 'store/spot/spot-actions';
import ParkingOptions from './parking-options';
import ParkingRestrictions from 'common/spot-details/parking-restrictions';
import AccessHours from 'common/spot-details/access-hours';
import AmenitiesList from 'common/spot-details/amenities-list';
import SpotDetailsPlaceholder from 'common/spot-details/placeholder';
import SpotImages from 'common/spot-details/spot-images';
import SpotNotes from 'common/spot-details/operator-and-admin-notes';
import ThingsYouShouldKnow from 'common/spot-details/things-you-should-know';
import GettingThere from 'common/spot-details/getting-there';
import OnlineCommuterRateModal from 'common/spot-details/online-commuter-rate-modal';
import RateDetailsModal from 'common/spot-details/rate-details-modal';
import HowToRedeem from 'common/spot-details/redemption-instructions';
import Reviews from 'common/spot-details/reviews';
import PaymentMethodsIcons from 'common/payment-methods-icons';
import FAQPanels from 'common/spot-details/faq';
import TrustSealValueProps from 'common/spot-details/trust-seal-and-value-props';
import {showSpotsLeft} from 'utils/spot-left';
import StarRating from 'common/star-rating';
import EditTimesModal from '../edit-times-modal/spot-details';
import trackAppleMapsViewNearbySpotsClicked from 'segment/events/apple-maps-view-nearby-spots-clicked';
import {
    getRankOfSpot,
    getSelectedSpot,
    getSelectedSpotDistance,
} from 'pages/search-transient-event-monthly/utils/spot-details-utils';
import ErrorUtils from 'utils/error-utils';
import SpotValueProps from 'pages/search-transient-event-monthly/components/spot-details-transient/spot-value-props';
import trackOpenedSpotDetails from 'segment/events/opened-spot-details';
import {withRouter} from 'react-router-dom/';
import {CarouselVariant} from 'common/image-carousel';
import {FACILITY_SUMMARY_VIEW_TYPES} from 'common/facility-summary/utils';
import SpotDetailPriceBreakdownModal from 'common/spot-details/price-breakdown-modal/SpotDetailPriceBreakdownModal';
import {getVehicleInfo} from 'store/spots/spots-selectors';
import {ReservationSummary} from './reservation-summary/ReservationSummary';
import {withOptimizely} from '@optimizely/react-sdk';
import {withIsMobile} from 'hooks/use-is-mobile';
import {addRebookParamToUrl} from 'pages/search-transient-event-monthly/utils/rebook-reservation-utils';
import ShuttleInformation from 'pages/spot-details-airport/components/shuttle-information';
import AirportSpotTag from '../spot-card-airport/AirportSpotTag';
import ResponsivePrice from './responsive-price/ResponsivePrice';
import AirportDistance from '../spot-card-airport/AirportDistance';

const window = getWindow();

// For showing Spots Left badge we rely on Search V2 API data
// as it accounts for Inventory Control and thus is more accurate
// Reference thread: https://spothero.slack.com/archives/C025ELN6L2G/p1666811071313479
const computeShowSpotsLeftState = spots => {
    const spot = getSelectedSpot(spots);
    const availableSpaces = spot?.availability?.availableSpaces;
    const available = spot?.availability?.available;
    const facilityType = spot?.facilityType;
    const isSpotsLeftBannerVisible = showSpotsLeft({
        available,
        availableSpaces,
        facilityType,
    });

    return {
        availableSpaces,
        isSpotsLeftBannerVisible,
    };
};

export const getSpotVehicleRestrictionReasonFromAvailability = (
    spot,
    showSpotUnavailable
) => {
    if (!spot || !showSpotUnavailable) {
        return false;
    }

    const {availability = {}} = spot;

    const isVehicleRestrictionAppliedToSpot = Boolean(
        availability?.unavailableReasons?.length === 1 &&
            availability?.unavailableReasons?.[0] === 'Vehicle Size Exceeded'
    );

    return isVehicleRestrictionAppliedToSpot;
};

class SpotDetails extends Component {
    static propTypes = {
        isMobile: PropTypes.bool,
        user: PropTypes.object,
        spotId: PropTypes.number,
        city: PropTypes.object,
        destination: PropTypes.object,
        event: PropTypes.object,
        searchRequest: PropTypes.object,
        referrerAffiliate: PropTypes.string,
        onCloseClick: PropTypes.func.isRequired,
        pushTo: PropTypes.func.isRequired,
        onUpdateData: PropTypes.func.isRequired,
        appleMapsPartner: PropTypes.bool.isRequired,
        appRating: PropTypes.shape({
            rating: PropTypes.number,
            ratingCount: PropTypes.number,
        }),
        spots: PropTypes.array,
        spotDetailReferrer: PropTypes.string,
        activeAmenities: PropTypes.array,
        updateSelectedSpot: PropTypes.func,
        onEditTimesModalClose: PropTypes.func,
        promptForTime: PropTypes.bool,
        vehicleInfoId: PropTypes.string,
        showTotal: PropTypes.bool,
        isRebookingReservation: PropTypes.bool,
        optimizely: PropTypes.object,
    };
    _container = createRef();
    constructor(props) {
        super(props);

        const {promptForTime, appleMapsPartner, isMobile, destination} = props;
        const editTimesModalOpen = appleMapsPartner
            ? appleMapsPartner && isMobile && isEmpty(destination)
            : promptForTime && isMobile;

        this.state = {
            updatedSpotData: null,
            isShowing: false,
            isLoadingRates: true,
            isRateLoadError: false,
            showSpotUnavailable: false,
            showRateDetailsModal: false,
            showPriceBreakdownModal: false,
            showOnlineCommuterRateModal: false,
            rateDetailsSection: null,
            showStickySpotDetailsReservationSummary: false,
            editTimesModalOpen,
            isSpotsLeftBannerVisible: false,
            availableSpaces: null,
        };
        this._initSpotDetails = this._initSpotDetails.bind(this);
    }

    componentDidMount() {
        DOMUtils.addClass('html', 'SpotDetails-open');

        this._initSpotDetails();

        window.addEventListener('keydown', this._onKeyDown);
    }

    componentDidUpdate(prevProps) {
        const {starts: newStarts, ends: newEnds} = this.props.searchRequest;
        const {starts: prevStarts, ends: prevEnds} = prevProps.searchRequest;
        const hasSearchRequestTimeChanged = !(
            prevStarts === newStarts && prevEnds === newEnds
        );
        const hasVehicleChanged =
            prevProps.vehicleInfoId !== this.props.vehicleInfoId;

        if (
            prevProps.spotId !== this.props.spotId ||
            hasSearchRequestTimeChanged ||
            hasVehicleChanged
        ) {
            this._initSpotDetails();
        }
    }

    componentWillUnmount() {
        this._isUnmounted = true;

        DOMUtils.removeClass('html', 'SpotDetails-open');

        if (this._rateLoader) {
            this._rateLoader.cancel();
            this._rateLoader = null;
        }

        window.removeEventListener('keydown', this._onKeyDown);
    }

    _onKeyDown = evt => {
        // close spot details if escape is pressed on keyboard
        if (evt.which === 27) {
            this._onCloseClick();
        }
    };

    _onRetryClick = () => {
        this._loadRates();
    };

    _onScroll = () => {
        const {isLoadingRates, isRateLoadError} = this.state;

        if (isLoadingRates || isRateLoadError) {
            return;
        }

        // Add defensive check to prevent errors when DOMUtils.el is undefined
        if (!DOMUtils?.el) {
            return;
        }

        const summaryEl = DOMUtils.el('.SpotDetailsReservationSummary')[0];

        // Early return if element is not found
        if (!summaryEl) {
            return;
        }

        const {top: summaryTop} = DOMUtils.getDocumentOffset(summaryEl);
        const summaryHeight = DOMUtils.height(summaryEl);
        const headerHeight = DOMUtils.height('.Header');
        const summaryBottom = summaryTop + summaryHeight;
        const shouldShowSticky = summaryBottom < headerHeight;

        if (
            shouldShowSticky !==
            this.state.showStickySpotDetailsReservationSummary
        ) {
            this.setState({
                showStickySpotDetailsReservationSummary: shouldShowSticky,
            });
        }
    };

    _onMultiRateChange = rateId => {
        const {updatedSpotData: spotData} = this.state;
        const rateType = 'hourly_rates';
        const newRate = find(spotData[rateType], {
            fullRule: rateId,
        });
        const newSpotData = {
            ...spotData,
            selectedRate: false,
        };

        if (newRate) {
            newSpotData.selectedRate = newRate;
        }

        this.setState({
            updatedSpotData: newSpotData,
        });
    };

    _onShowRateDetailsClick = section => {
        this.setState({
            showRateDetailsModal: true,
            rateDetailsSection: isString(section) ? section : null,
        });
    };

    _onShowRateDetailsClick = () => {
        this.setState({
            showPriceBreakdownModal: true,
        });
    };

    _onOnlineCommuterRateClick = () => {
        this.setState({
            showOnlineCommuterRateModal: true,
        });
    };

    _onBookSpotClick = evt => {
        evt.preventDefault();

        const {
            pushTo,
            searchRequest,
            isRebookingReservation,
            optimizely,
            user: {isAdmin},
        } = this.props;
        const {updatedSpotData: spotData} = this.state;

        if (!spotData.selectedRate) {
            return null;
        }

        const shouldSend = SpotUtils.shouldSendToExpressCheckout({
            spotData,
            searchRequest,
            isRebookingReservation,
            isAdmin,
            optimizely,
        });

        const {
            selectedRate: {fullUrl, expressCheckoutUrl},
        } = spotData;

        if (shouldSend) {
            window.location.href = expressCheckoutUrl;
        } else {
            pushTo(
                addRebookParamToUrl(
                    fullUrl,
                    searchRequest?.rebook_reservation_id
                )
            );
        }
    };

    _onHideAllModals = () => {
        this.setState({
            showRateDetailsModal: false,
            showOnlineCommuterRateModal: false,
            showPriceBreakdownModal: false,
        });
    };

    _onCloseClick = () => {
        const {onCloseClick} = this.props;

        this.setState({
            isShowing: false,
            showStickySpotDetailsReservationSummary: false,
        });

        onCloseClick();
    };

    _onToggleEditTimesModal = () => {
        if (this.props.onEditTimesModalClose) {
            this.props.onEditTimesModalClose();
        }

        this.setState(({editTimesModalOpen}) => ({
            editTimesModalOpen: !editTimesModalOpen,
        }));
    };

    _onHandleCloseClick = () => {
        const {destination, onUpdateData} = this.props;

        // clear Apple Maps experience unless it's a destination / event
        if (isEmpty(destination)) {
            onUpdateData({
                partner: null,
            });

            // Legacy comment from when it was goBack
            // A user can click browser back button or this button to close the spot details
            // They both should have the same behaviour
        }
        this._onCloseClick();
    };

    _onViewNearbySpotsClick = () => {
        trackAppleMapsViewNearbySpotsClicked({
            facility: this.state.updatedSpotData?.facility,
            location: 'Spot Details',
        });

        this._onHandleCloseClick();
    };

    _initSpotDetails() {
        this._loadRates();

        this.setState({
            isShowing: true,
        });

        trackOpenedSpotDetails({
            referrer: this.props.spotDetailReferrer,
        });
    }

    _trackSegmentEvents(spot) {
        const {
            user: {id: userId, status: userStatus},
            searchRequest,
            referrerAffiliate,
            city,
            destination,
            event,
            spotDetailReferrer,
            spots,
        } = this.props;
        const {monthly: isMonthly, starts, ends, referrer} = searchRequest;
        const {isSpotsLeftBannerVisible} = this.state;
        const selectedSpot = getSelectedSpot(spots);
        const visualFlags = selectedSpot?.visualFlags || [];
        const tag = visualFlags[0]?.title;

        const rank = Array.isArray(this.props.spots)
            ? getRankOfSpot(this.props.spots)
            : undefined;

        trackSpotDetails({
            userId,
            city,
            destination,
            event,
            isMonthly,
            starts,
            ends,
            spot: {
                ...spot,
                facility: {
                    ...spot?.facility,
                    tag,
                    visual_flags: visualFlags,
                },
            },
            referrer,
            referrerAffiliate,
            spotsLeftDisplayed: isSpotsLeftBannerVisible,
            spotDetailReferrer,
            rank,
        });

        trackAvailabilityCheck({
            searchRequest,
            city,
            destination,
            event,
            spot,
            userStatus,
            screen: 'spot details',
        });
    }

    _loadRates() {
        const {
            spotId,
            searchRequest,
            city,
            event,
            spots,
            activeAmenities,
            vehicleInfoId,
        } = this.props;
        const {
            monthly: isMonthly,
            airport: isAirport,
            starts,
            ends,
            rebook_reservation_id: rebookId,
            powerBookingPeriods,
            powerBooking: isPowerBooking,
            powerBookingSource,
        } = searchRequest;
        const eventId = event ? event.id : null;

        this.setState({
            isLoadingRates: true,
            isRateLoadError: false,
        });

        this._rateLoader = SpotUtils.loadRatesSpotDetail({
            spotId,
            starts,
            ends,
            powerBookingPeriods,
            isPowerBooking,
            powerBookingSource,
            isMonthly,
            isAirport,
            timezone: city.timezone,
            searchDataParams: {
                monthly: isMonthly,
                starts,
                ends,
                eid: eventId,
            },
            spot: null,
            eventId,
            rebookId,
            activeAmenities,
            vehicleInfoId,
            onSuccessWithRates: updatedSpotData => {
                this.setState({
                    updatedSpotData,
                    showSpotUnavailable: false,
                    isLoadingRates: false,
                    ...computeShowSpotsLeftState(spots),
                });

                this._trackSegmentEvents(updatedSpotData);
                this._rateLoader = null;
            },
            onSuccessWithoutRates: partialSpotData => {
                GTMUtils.push({
                    event: 'GAEvent',
                    eventCategory: 'automatic',
                    eventAction: 'rate_unavailable',
                    eventLabel: 'transient_rate_toggle',
                    eventValue: undefined, // eslint-disable-line no-undefined
                    hitCallback: undefined, // eslint-disable-line no-undefined
                });

                this.setState({
                    updatedSpotData: partialSpotData,
                    showSpotUnavailable: true,
                    isLoadingRates: false,
                    ...computeShowSpotsLeftState([]),
                });

                this._rateLoader = null;
            },
            onError: error => {
                if (this._isUnmounted) {
                    return;
                }

                if (searchRequest?.partner === 'apple') {
                    ErrorUtils.sendSentryMessage({
                        error,
                        customErrorMessage: 'Apple Maps problem with spot',
                    });
                }

                this.setState({
                    updatedSpotData: null,
                    showSpotUnavailable: true,
                    isLoadingRates: false,
                    isRateLoadError: true,
                    ...computeShowSpotsLeftState([]),
                });
                this._rateLoader = null;
            },
        });
    }

    _renderSpotImages(facility) {
        const {
            parking_spot_id: facilityId,
            title: facilityTitle,
            images: facilityImages,
        } = facility;

        return (
            <SpotImages
                key={facilityId}
                images={facilityImages}
                alt={facilityTitle}
                carouselVariant={CarouselVariant.SINGLE}
            />
        );
    }

    _renderFacilitySummary(
        spot,
        appleMapsPartner,
        spotUnavailableDueToVehicleRestriction
    ) {
        const {
            user: {isAdmin},
            spots,
            searchRequest,
            isMobile,
        } = this.props;
        const {airport: isAirport, starts, ends} = searchRequest;
        const {isSpotsLeftBannerVisible, availableSpaces} = this.state;
        const {facility = {}} = spot;
        const hasRateData = Boolean(spot?.selectedRate);
        const selectedSpot = getSelectedSpot(spots);
        const distance = getSelectedSpotDistance(selectedSpot);
        const spotValue = {
            ...spot,
            facility: {
                ...facility,
                visual_flags: selectedSpot?.visualFlags || [],
            },
            rating: {
                average: facility?.rating_info?.star_rating || 0,
                count: facility?.rating_info?.number_of_ratings || 0,
            },
            distance,
            allAmenities: hasRateData
                ? spot.selectedRate.amenities.map(amenity => {
                      return {type: amenity.slug};
                  })
                : [],
        };
        const {isExtended} = hasRateData
            ? TimeUtils.getTimeExtension({
                  actualStartDateTimeObj: dayjs(spot.selectedRate.starts),
                  actualEndDateTimeObj: dayjs(spot.selectedRate.ends),
                  requestedStartDateTimeObj: dayjs(starts),
                  requestedEndDateTimeObj: dayjs(ends),
                  isMonthly: false,
              })
            : false;

        return isAirport ? (
            <Box
                sx={{
                    '.FacilitySummary-container': {height: 'auto'},
                    '.FacilitySummary-content': {
                        display: 'flex',
                        columnGap: {tablet: '12px', base: '8px'},
                        marginTop: '12px',
                        flexWrap: 'wrap',
                    },
                    '.FacilitySummary': {
                        borderBottom: 'none',
                        '.chakra-heading': {fontSize: '0px'},
                    },
                    '.FacilitySummary-title': {
                        fontSize: '16px',
                        maxWidth: 'stretch',
                    },
                    '.FacilitySummary-FormattedPrice': {
                        fontSize: '20px',
                        fontWeight: '600',
                        'sup:last-child': {
                            fontSize: '100%',
                            top: 0,
                        },
                        'sup:first-child': {
                            fontWeight: '700',
                            fontSize: '14px',
                        },
                    },
                    "[data-testid='PriceBreakdown-Button']": {marginTop: '4px'},
                    '.AirportSpotTag': {
                        marginBottom: '0',
                    },
                }}
            >
                <FacilitySummaryV2
                    spot={spotValue}
                    showPrice={hasRateData && !isMobile}
                    showImages={false}
                    AirportTag={
                        <AirportSpotTag
                            tag={selectedSpot?.tag}
                            title={selectedSpot?.title}
                            spotsLeft={
                                isSpotsLeftBannerVisible
                                    ? availableSpaces
                                    : null
                            }
                        />
                    }
                    isAdmin={isAdmin}
                    isShowSpotsLeftVisible={false}
                    view={FACILITY_SUMMARY_VIEW_TYPES.SPOT_DETAIL}
                    AirportDistance={<AirportDistance spot={selectedSpot} />}
                    CTAs={[]}
                    showInOut
                />
                {isExtended && (
                    <Alert
                        marginBottom={4}
                        px={6}
                        mx={3}
                        width="auto"
                        status="success"
                        title="Your reservation was extended at no extra charge!"
                        icon={ClockPlus}
                        sx={{
                            '.chakra-alert__title': {fontSize: '12px'},
                        }}
                    />
                )}
            </Box>
        ) : (
            <FacilitySummary
                isAdmin={isAdmin}
                spot={spotValue}
                availableSpaces={availableSpaces}
                showImages={false}
                showPrice={false}
                appleMapsPartner={appleMapsPartner}
                showFullTitle
                showAdminLink
                isSpotsLeftBannerVisible={isSpotsLeftBannerVisible}
                view={FACILITY_SUMMARY_VIEW_TYPES.SPOT_DETAIL}
                showVehicleErrorBanner={spotUnavailableDueToVehicleRestriction}
            />
        );
    }

    _renderParkingOptions(spot) {
        const {selectedRate, hourly_rates: hourlyRates} = spot;

        return (
            hourlyRates.length > 1 && (
                <ParkingOptions
                    availableRates={hourlyRates}
                    selectedRate={selectedRate}
                    onMultiRateChange={this._onMultiRateChange}
                    onRateDetailsClick={this._onShowRateDetailsClick}
                />
            )
        );
    }

    _renderParkingRestrictions(spot) {
        const {selectedRate} = spot;
        const {online_commuter_rate: onlineCommuterRate} = selectedRate;

        return (
            <ParkingRestrictions
                hasOnlineCommuterRate={Boolean(onlineCommuterRate)}
                onOnlineCommuterRateClick={this._onOnlineCommuterRateClick}
            />
        );
    }

    _renderAirportReservationSummary(spot) {
        const dateFormat = 'MMM D, h:mm A';
        const {selectedRate} = spot;

        const tableCellStyles = {
            display: 'table-cell',
            padding: '12px 24px',
        };
        const labelStyles = {
            fontSize: 'sm',
            fontWeight: '600',
            color: 'text.secondary.light',
        };
        const dateStyles = {
            fontSize: 'base',
            fontWeight: '600',
            color: 'black',
        };
        const borderStyles = {
            border: '1px solid #cbd4de',
            borderLeft: 'none',
            borderRight: 'none',
        };

        return (
            <Flex display="table" width="100%" {...borderStyles}>
                <Box
                    {...tableCellStyles}
                    paddingRight="0"
                    borderRight="1px solid #cbd4de"
                >
                    <Text {...labelStyles}>Enter After</Text>
                    <Text {...dateStyles}>
                        {dayjs(selectedRate.starts).format(dateFormat)}
                    </Text>
                </Box>
                <Box {...tableCellStyles}>
                    <Text {...labelStyles}>Exit Before</Text>
                    <Text {...dateStyles}>
                        {dayjs(selectedRate.ends).format(dateFormat)}
                    </Text>
                </Box>
            </Flex>
        );
    }

    _renderUpdatedCTAs(spot) {
        const {
            user: {isAdmin},
        } = this.props;
        const {isMobile} = this.props;
        const {selectedRate} = spot;
        const {showSpotUnavailable} = this.state;
        const disableBookNow = !selectedRate || selectedRate?.unavailable;

        const styles = isMobile
            ? {
                  paddingBottom: isAdmin ? '52px' : '68px',
                  pt: isAdmin ? 0 : 4,
                  zIndex: 300,
                  position: 'sticky',
                  backgroundColor: 'white',
                  mx: 0,
                  boxShadow: '0px -2px 4px -2px black',
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  px: 4,
              }
            : {
                  mx: 4,
              };

        return (
            <Box {...styles}>
                {isMobile && !showSpotUnavailable && (
                    <Box
                        display="flex"
                        alignItems="center"
                        sx={{
                            '.FacilitySummary-FormattedPrice': {
                                fontSize: '24px',
                                fontWeight: '600',
                                'sup:last-child': {
                                    fontSize: '100%',
                                    top: 0,
                                },
                                'sup:first-child': {
                                    fontWeight: '700',
                                    fontSize: '14px',
                                },
                            },
                            '.FacilitySummary-price-container': {
                                alignItems: 'flex-start',
                            },
                            '[data-testid="PriceBreakdown-Button"]': {
                                marginLeft: '10px',
                            },
                        }}
                    >
                        <ResponsivePrice spot={spot} />
                    </Box>
                )}
                <Box>
                    {!showSpotUnavailable && (
                        <Button
                            variant="primary"
                            width="100%"
                            className="SpotDetailsReservationSummary-book-now Button-primary-booking-flow"
                            onClick={this._onBookSpotClick}
                            mb={4}
                            isDisabled={disableBookNow}
                        >
                            Book Now
                        </Button>
                    )}
                    {isAdmin && (
                        <Button
                            variant="tertiary"
                            fontSize="base"
                            display="block"
                            mb={4}
                            textAlign="center"
                            textDecoration="underline"
                            as="a"
                            href={`/admin/spots/${spot?.spotId}`}
                        >
                            Access Admin View
                        </Button>
                    )}
                </Box>
            </Box>
        );
    }

    _renderShuttleInformation(spot) {
        const {destination} = this.props;
        const facilityTitle = spot?.facility?.title;

        return (
            <AccordionItem mx={2}>
                <AccordionButton>
                    <AccordionIcon />
                    <Text
                        fontFamily="body"
                        flex="1"
                        textAlign="left"
                        fontWeight="600"
                        fontSize="16px"
                    >
                        Shuttle Information
                    </Text>
                </AccordionButton>
                <AccordionPanel>
                    <Box
                        className="SpotDetails-section"
                        sx={{
                            '.ShuttleInformation': {
                                borderBottom: 'none',
                                margin: 0,
                                padding: 0,
                                '.subtitle': {fontSize: '16px'},
                            },
                            '.ShuttleInformation-details-value': {
                                fontWeight: '400',
                            },
                            '.ShuttleInformation-details': {
                                paddingRight: '16px',
                                '.no-images': {
                                    flexDirection: 'column',
                                },
                            },
                        }}
                    >
                        <ShuttleInformation
                            shuttle={spot?.shuttle}
                            facilityTitle={facilityTitle}
                            iataCode={destination?.airport?.iata_code}
                            showHeading={false}
                        />
                    </Box>
                </AccordionPanel>
            </AccordionItem>
        );
    }

    _renderThingsYouShouldKnow(spot, appleMapsPartner) {
        const {selectedRate, facility} = spot;
        const {restrictions: facilityRestrictions} = facility;
        const {amenities: rateAmenities} = selectedRate;
        const hasInOutAmenity = Boolean(
            rateAmenities.find(amenity => amenity.slug === 'in-out')
        );

        return (
            <AccordionItem mx={2}>
                <AccordionButton>
                    <AccordionIcon />
                    <Text
                        fontFamily="body"
                        flex="1"
                        textAlign="left"
                        fontWeight="600"
                        fontSize="16px"
                    >
                        {appleMapsPartner
                            ? 'About This Spot'
                            : 'Things You Should Know'}
                    </Text>
                </AccordionButton>
                <AccordionPanel>
                    <ThingsYouShouldKnow
                        hasInOutAmenity={hasInOutAmenity}
                        restrictions={facilityRestrictions}
                        appleMapsPartner={appleMapsPartner}
                        showHeading={false}
                    />
                </AccordionPanel>
            </AccordionItem>
        );
    }

    _renderAmenities(spot) {
        const {selectedRate, facility} = spot;
        const {amenities_full: nonRateAmenities} = facility;

        return (
            <AccordionItem mx={2}>
                <AccordionButton>
                    <AccordionIcon />
                    <Text
                        fontFamily="body"
                        flex="1"
                        textAlign="left"
                        fontWeight="600"
                        fontSize="16px"
                    >
                        Amenities
                    </Text>
                </AccordionButton>
                <AccordionPanel>
                    <AmenitiesList
                        rate={selectedRate}
                        isMonthly={false}
                        nonRateAmenities={nonRateAmenities}
                        onShowRateDetailsClick={this._onShowRateDetailsClick}
                        showHeading={false}
                    />
                </AccordionPanel>
            </AccordionItem>
        );
    }

    _renderAccessHours(spot) {
        const {selectedRate, facility} = spot;
        const {access_hours_formatted: facilityAccessHours} = facility;
        const {
            access_hours_formatted: rateAccessHours,
            display_hours_comment: hoursComment,
        } = selectedRate;
        const showAccessHours = Boolean(
            rateAccessHours || hoursComment || !isEmpty(facilityAccessHours)
        );

        return (
            showAccessHours && (
                <AccordionItem mx={2}>
                    <AccordionButton>
                        <AccordionIcon />
                        <Text
                            fontFamily="body"
                            flex="1"
                            textAlign="left"
                            fontWeight="600"
                            fontSize="16px"
                        >
                            Access Hours
                        </Text>
                    </AccordionButton>
                    <AccordionPanel>
                        <AccessHours
                            rate={selectedRate}
                            facility={facility}
                            showHeading={false}
                        />
                    </AccordionPanel>
                </AccordionItem>
            )
        );
    }

    _renderRedemptionInstructions(facility) {
        const {
            redemption_instructions: facilityInstructions,
            post_purchase_instructions: postPurchaseInstructions,
        } = facility;

        return facilityInstructions ? (
            <AccordionItem mx={2}>
                <AccordionButton>
                    <AccordionIcon />
                    <Text
                        fontFamily="body"
                        flex="1"
                        textAlign="left"
                        fontWeight="600"
                        fontSize="16px"
                    >
                        How to Redeem
                    </Text>
                </AccordionButton>
                <AccordionPanel>
                    <HowToRedeem instructions={facilityInstructions} />
                </AccordionPanel>
            </AccordionItem>
        ) : (
            <AccordionItem mx={2}>
                <AccordionButton>
                    <AccordionIcon />
                    <Text
                        fontFamily="body"
                        flex="1"
                        textAlign="left"
                        fontWeight="600"
                        fontSize="16px"
                    >
                        How to Redeem
                    </Text>
                </AccordionButton>
                <AccordionPanel>
                    <span
                        dangerouslySetInnerHTML={{
                            __html: postPurchaseInstructions,
                        }}
                    />
                </AccordionPanel>
            </AccordionItem>
        );
    }

    _renderAirportRedemptionInstructions(facility) {
        const {
            redemption_instructions_arrival: arrivalInstructions,
            redemption_instructions_departure: departureInstructions,
        } = facility;

        return (
            <AccordionItem
                mx={2}
                sx={{
                    '.SpotDetails-section': {
                        borderTop: 'none',
                    },
                }}
            >
                <Heading as="h4">
                    <AccordionButton>
                        <AccordionIcon />
                        <Text
                            fontFamily="body"
                            flex="1"
                            textAlign="left"
                            fontWeight="600"
                            fontSize="16px"
                        >
                            How to Redeem
                        </Text>
                    </AccordionButton>
                </Heading>
                <AccordionPanel>
                    <Text>
                        You will receive an email with redemption instructions
                        after making a reservation.
                    </Text>
                    <HowToRedeem
                        sectionTitle="Arrival"
                        instructions={arrivalInstructions}
                    />
                    <HowToRedeem
                        sectionTitle="Departure"
                        instructions={departureInstructions}
                    />
                </AccordionPanel>
            </AccordionItem>
        );
    }

    _renderGettingThere(facility) {
        const {getting_here: gettingHere} = facility;

        return (
            <AccordionItem mx={2}>
                <AccordionButton>
                    <AccordionIcon />
                    <Text
                        fontFamily="body"
                        flex="1"
                        textAlign="left"
                        fontWeight="600"
                        fontSize="16px"
                    >
                        Getting There
                    </Text>
                </AccordionButton>
                <AccordionPanel>
                    {gettingHere && (
                        <GettingThere
                            instructions={gettingHere}
                            showHeading={false}
                        />
                    )}
                </AccordionPanel>
            </AccordionItem>
        );
    }

    _renderReviews(facility) {
        const {rating_info: ratingInfo} = facility;

        return (
            <AccordionItem mx={2}>
                <AccordionButton>
                    <AccordionIcon />
                    <Text
                        fontFamily="body"
                        flex="1"
                        textAlign="left"
                        fontWeight="600"
                        fontSize="16px"
                    >
                        Facility Reviews
                    </Text>
                </AccordionButton>
                <AccordionPanel>
                    <Reviews ratingInfo={ratingInfo} showHeading={false} />
                </AccordionPanel>
            </AccordionItem>
        );
    }

    _renderSpotNotes(spot) {
        const {operator_notes: operatorNotes, spot_notes: spotNotes} = spot;

        return (
            <>
                <AccordionItem mx={2}>
                    <AccordionButton>
                        <AccordionIcon />
                        <Text
                            fontFamily="body"
                            flex="1"
                            textAlign="left"
                            fontWeight="600"
                            fontSize="16px"
                        >
                            Spot Notes (Admin)
                        </Text>
                    </AccordionButton>
                    <AccordionPanel>
                        <SpotNotes type="spot" notes={spotNotes} />
                    </AccordionPanel>
                </AccordionItem>
                <AccordionItem mx={2}>
                    <AccordionButton>
                        <AccordionIcon />
                        <Text
                            fontFamily="body"
                            flex="1"
                            textAlign="left"
                            fontWeight="600"
                            fontSize="16px"
                        >
                            Operator Notes (Admin)
                        </Text>
                    </AccordionButton>
                    <AccordionPanel>
                        <SpotNotes type="operator" notes={operatorNotes} />
                    </AccordionPanel>
                </AccordionItem>
            </>
        );
    }

    _renderFooter(facility, canCancelHPEReservation, eventCancellationMinutes) {
        const {cancellation_allowed: cancellationAllowed} = facility;
        const hpeCancellationProps = {
            canCancelHPEReservation,
            eventCancellationMinutes,
        };

        return (
            <Box
                sx={{
                    '.FAQPanels-item': {border: 'none'},
                }}
            >
                <FAQPanels
                    cancellationAllowed={cancellationAllowed}
                    {...hpeCancellationProps}
                />
                <TrustSealValueProps />
            </Box>
        );
    }

    render() {
        const {
            isMobile,
            user: {isAdmin},
            appRating,
            city,
            destination,
            searchRequest: {starts, ends, airport: isAirport},
            appleMapsPartner,
            spotId,
            spots,
            updateSelectedSpot,
            promptForTime,
        } = this.props;
        const {
            updatedSpotData: spot,
            isShowing,
            isLoadingRates,
            isRateLoadError,
            rateDetailsSection,
            showStickySpotDetailsReservationSummary,
            showRateDetailsModal,
            showPriceBreakdownModal,
            showOnlineCommuterRateModal,
            editTimesModalOpen,
            showSpotUnavailable,
        } = this.state;
        const classes = classNames('SpotDetails', {
            'SpotDetails-showing': isShowing,
        });
        const shimClasses = classNames('SpotDetails-shim', 'shim', {
            'SpotDetails-shim-showing': isShowing,
        });
        // These booleans are used to conditionally display subcomponents
        // Some subcomponents are rate-dependent, while others can be displayed if no rate is available
        const hasFacilityData = Boolean(spot);
        const hasFacilityAndRateData = Boolean(spot?.selectedRate);
        const cancellationAllowedValue =
            hasFacilityAndRateData && spot?.facility?.cancellation_allowed;
        const cancellationAllowed =
            Boolean(cancellationAllowedValue) &&
            cancellationAllowedValue !== 'no';

        const eventCancellationMinutes = spot?.event_cancellation_minutes || 0;
        const canCancelHPEReservation = SpotUtils.canCancelHPEReservation({
            reservationStart: starts,
            reservationTimezone: city?.timezone,
            eventCancellationMinutes,
        });
        const hpeCancellationProps = {
            canCancelHPEReservation,
            eventCancellationMinutes,
        };
        // check if this is a high-profile event that is past the cancellation threshold
        const isNonRefundable =
            cancellationAllowed &&
            eventCancellationMinutes > 0 &&
            !canCancelHPEReservation;

        const spotUnavailableDueToVehicleRestriction = getSpotVehicleRestrictionReasonFromAvailability(
            spot,
            showSpotUnavailable
        );

        return (
            <>
                <Helmet>
                    <meta name="robots" content="noindex, follow" />
                </Helmet>
                <div className={shimClasses} onClick={this._onCloseClick} />
                <Box
                    ref={this._container}
                    className={classes}
                    onScroll={throttle(this._onScroll, 500)}
                    sx={{
                        height: isMobile && isAirport ? 'auto' : '100%',
                        bottom: isMobile && isAirport ? '105px' : '0',
                    }}
                >
                    {isMobile ? (
                        (!appleMapsPartner || !isEmpty(destination)) && (
                            <Button
                                _hover={{
                                    backgroundColor: 'transparent',
                                    borderColor: 'transparent',
                                }}
                                position="absolute"
                                backgroundColor="transparent"
                                width={14}
                                height={14}
                                boarderRadius="0"
                                paddingTop={0}
                                paddingLeft={0}
                                paddingRight={0}
                                paddingBottom={0}
                                borderColor="transparent"
                                className="SpotDetails-back"
                                onClick={this._onHandleCloseClick}
                                fontSize="sm"
                            >
                                {/* span allows CSS to create larger tap target for this button */}
                                <span>
                                    <IconArrowLeft />
                                </span>
                            </Button>
                        )
                    ) : (
                        <Box
                            className="SpotDetails-close-container"
                            width="100%"
                        >
                            <Button
                                _hover={{
                                    backgroundColor: 'white',
                                    borderColor: 'white',
                                }}
                                position="absolute"
                                backgroundColor="white"
                                width={10}
                                boarderRadius="50%"
                                paddingTop={0}
                                paddingLeft={0}
                                paddingRight={0}
                                paddingBottom={0}
                                borderColor="white"
                                className="SpotDetails-close"
                                onClick={this._onCloseClick}
                                fontSize="sm"
                            >
                                <IconTimes />
                            </Button>
                        </Box>
                    )}
                    {hasFacilityData && this._renderSpotImages(spot.facility)}
                    {hasFacilityData &&
                        this._renderFacilitySummary(
                            spot,
                            appleMapsPartner,
                            spotUnavailableDueToVehicleRestriction
                        )}
                    {hasFacilityAndRateData &&
                        !isAirport &&
                        this._renderParkingOptions(spot)}
                    {hasFacilityAndRateData &&
                        this._renderParkingRestrictions(spot)}
                    {hasFacilityData && !isAirport && (
                        <ReservationSummary
                            spot={spot}
                            isSticky={false}
                            isStickyVersionShowing={false}
                            isNonRefundable={isNonRefundable}
                            isLoadingRates={isLoadingRates}
                            showSpotUnavailable={showSpotUnavailable}
                            spotUnavailableDueToVehicleRestriction={
                                spotUnavailableDueToVehicleRestriction
                            }
                            onBookSpotClick={this._onBookSpotClick}
                            onToggleEditTimesModal={
                                this._onToggleEditTimesModal
                            }
                            onShowRateDetailsClick={
                                this._onShowRateDetailsClick
                            }
                        />
                    )}
                    {cancellationAllowed && !isAirport && (
                        <SpotValueProps
                            cancellationAllowed={cancellationAllowed}
                            {...hpeCancellationProps}
                        />
                    )}
                    {hasFacilityData && !isAirport && (
                        <Box
                            padding={appleMapsPartner ? 4 : 0}
                            bgColor={
                                appleMapsPartner ? 'gray.light' : 'transparent'
                            }
                            margin={appleMapsPartner ? 4 : 0}
                            marginTop={0}
                        >
                            <Flex
                                marginX={!isMobile && appleMapsPartner ? 0 : 4}
                                marginBottom={4}
                                justifyContent="center"
                            >
                                <PaymentMethodsIcons
                                    showAlternatePaymentOptions
                                    alignment="center"
                                />
                            </Flex>
                            {appleMapsPartner && (
                                <Flex
                                    alignItems="center"
                                    justifyContent="center"
                                >
                                    <Image
                                        className="digicert-seal-image"
                                        cloudinaryImageId="logos/digicert-secured-seal"
                                        width={100}
                                        height={56}
                                        alt="Norton DigiCert Seal"
                                    />
                                    <Flex
                                        flexDirection="column"
                                        paddingLeft={3}
                                        justifyContent="center"
                                    >
                                        <Text
                                            variant="body2"
                                            fontWeight="semibold"
                                        >
                                            Top Rated App
                                        </Text>
                                        <Flex justifyContent="end">
                                            {appRating?.rating ? (
                                                <StarRating
                                                    stars={appRating?.rating}
                                                />
                                            ) : null}
                                            <Text
                                                variant="caption"
                                                marginLeft={1}
                                            >
                                                {appRating?.rating}/5 iOS
                                            </Text>
                                        </Flex>
                                    </Flex>
                                </Flex>
                            )}
                        </Box>
                    )}
                    {appleMapsPartner && (
                        <Button
                            variant="tertiary"
                            onClick={this._onViewNearbySpotsClick}
                            padding={3}
                            margin="auto"
                            display="block"
                            fontSize="sm"
                        >
                            View More Spots Nearby
                        </Button>
                    )}
                    {isLoadingRates && <SpotDetailsPlaceholder />}
                    {isRateLoadError && (
                        <Alert
                            status="error"
                            description="There was an error retrieving the facility
                            information."
                            showIcon
                            buttonText="Retry"
                            marginX={4}
                            buttonProps={{
                                onClick: this._onRetryClick,
                            }}
                            width="auto"
                            borderRadius="md"
                        />
                    )}
                    {isAirport &&
                        hasFacilityAndRateData &&
                        this._renderAirportReservationSummary(spot)}
                    {isAirport &&
                        hasFacilityData &&
                        !isMobile &&
                        this._renderUpdatedCTAs(spot)}
                    <Accordion
                        allowToggle
                        allowMultiple
                        defaultIndex={[0, 1, 2, 3, 4]}
                        sx={{
                            '.SpotDetails-section': {
                                borderTop: 'none',
                                margin: 0,
                                paddingLeft: '16px',
                            },
                        }}
                    >
                        {isAirport &&
                            hasFacilityData &&
                            this._renderShuttleInformation(spot)}
                        {hasFacilityAndRateData &&
                            this._renderThingsYouShouldKnow(
                                spot,
                                appleMapsPartner
                            )}
                        {hasFacilityAndRateData && this._renderAmenities(spot)}
                        {hasFacilityAndRateData &&
                            this._renderAccessHours(spot)}
                        {hasFacilityData &&
                            !isAirport &&
                            this._renderRedemptionInstructions(spot.facility)}
                        {hasFacilityData && this._renderReviews(spot.facility)}
                        {hasFacilityData &&
                            this._renderGettingThere(spot.facility)}
                        {hasFacilityData &&
                            isAirport &&
                            this._renderAirportRedemptionInstructions(
                                spot.facility
                            )}
                        {isAdmin &&
                            hasFacilityData &&
                            this._renderSpotNotes(spot)}
                    </Accordion>
                    {hasFacilityData &&
                        this._renderFooter(
                            spot.facility,
                            canCancelHPEReservation,
                            eventCancellationMinutes
                        )}
                    {hasFacilityData && (
                        <ReservationSummary
                            spot={spot}
                            isSticky
                            isStickyVersionShowing={
                                showStickySpotDetailsReservationSummary
                            }
                            isNonRefundable={false}
                            isLoadingRates={isLoadingRates}
                            showSpotUnavailable={showSpotUnavailable}
                            onBookSpotClick={this._onBookSpotClick}
                            onToggleEditTimesModal={
                                this._onToggleEditTimesModal
                            }
                            onShowRateDetailsClick={
                                this._onShowRateDetailsClick
                            }
                        />
                    )}
                </Box>
                {isAirport &&
                    hasFacilityAndRateData &&
                    isMobile &&
                    this._renderUpdatedCTAs(spot)}
                {showRateDetailsModal && hasFacilityAndRateData && (
                    <RateDetailsModal
                        spot={spot}
                        section={rateDetailsSection}
                        onHidden={this._onHideAllModals}
                    />
                )}
                {showPriceBreakdownModal && (
                    <SpotDetailPriceBreakdownModal
                        spot={spot}
                        onHidden={this._onHideAllModals}
                    />
                )}

                {showOnlineCommuterRateModal && hasFacilityAndRateData && (
                    <OnlineCommuterRateModal
                        title="Commuter Rate"
                        description={
                            spot.selectedRate?.online_commuter_rate_description
                        }
                        onHidden={this._onHideAllModals}
                        hideContentTitle
                    />
                )}

                {(appleMapsPartner || promptForTime) && isMobile ? (
                    <EditTimesModal
                        isOpen={editTimesModalOpen}
                        showExpandedVersion={
                            promptForTime || isEmpty(destination)
                        }
                        onClose={this._onToggleEditTimesModal}
                        facility={hasFacilityData ? spot.facility : null}
                        isAdmin={isAdmin}
                        timezone={city.timezone}
                        starts={starts}
                        ends={ends}
                        showSpotUnavailable={showSpotUnavailable}
                        isLoading={isLoadingRates}
                        onBackToSearch={this._onHandleCloseClick}
                        spotId={spotId}
                        spots={spots}
                        updateSelectedSpot={updateSelectedSpot}
                        errorLoadingRates={isRateLoadError}
                    />
                ) : null}
            </>
        );
    }
}

const mapStateToProps = state => {
    const {
        city: {data: city},
        destination: {data: destination},
        event: {data: event},
        user: {data: user},
        search: {
            data: {referrerAffiliate},
        },
        searchRequest,
        spot: {spotDetailReferrer},
        checkout: {isRebookingReservation},
        spots: {data: spots},
        appRating: {data: appRating},
        filters: {activeAmenities, showTotal},
    } = state;
    const vehicleInfoId = getVehicleInfo(state)?.id;

    const appleMapsPartner =
        searchRequest.partner === 'apple' ||
        searchRequest.partner === 'appleTest';

    return {
        city,
        destination,
        event,
        user,
        referrerAffiliate,
        searchRequest,
        appRating,
        appleMapsPartner,
        spots,
        spotDetailReferrer,
        activeAmenities,
        vehicleInfoId,
        showTotal,
        isRebookingReservation,
    };
};

const mapDispatchToProps = {
    pushTo: push,
    onUpdateData: updateData,
    updateSelectedSpot: updateSelected,
};

const enhance = compose(
    withRouter,
    withIsMobile,
    withOptimizely,
    connect(mapStateToProps, mapDispatchToProps)
);

export default enhance(SpotDetails);
